import React from "react";
import { Link } from "react-router";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  Grid2 as Grid,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AutoStoriesIcon from "@mui/icons-material/AutoStories";
import HeadphonesIcon from "@mui/icons-material/Headphones";
import NewspaperIcon from "@mui/icons-material/Newspaper";

import { Avatar } from "@shared/components";
import { ReceivedBook, ReceivedBookBook } from "@modules/receivedBooks/types";
import { ReceivedBookMonthlyReportPeriod } from "@modules/receivedBooks/queries/getMonthlyReport/types";

const FALLBACK_COVER = "/images/image-icon-picture-not-available-picturer.jpeg";

interface ReceivedBookPeriodsProps {
  periods?: ReceivedBookMonthlyReportPeriod[];
  loading?: boolean;
}

interface GroupedBooks {
  books: ReceivedBook[];
  audiobooks: ReceivedBook[];
  magazines: ReceivedBook[];
}

function groupByType(items: ReceivedBook[]): GroupedBooks {
  return items.reduce<GroupedBooks>(
    (acc, item) => {
      const type = item.book?.type;
      if (type === "audiobook") acc.audiobooks.push(item);
      else if (type === "magazine") acc.magazines.push(item);
      else acc.books.push(item);
      return acc;
    },
    { books: [], audiobooks: [], magazines: [] },
  );
}

function dedupeByBook(items: ReceivedBook[]): ReceivedBookBook[] {
  const seen = new Map<number, ReceivedBookBook>();
  for (const item of items) {
    if (item.book && !seen.has(item.book.id)) {
      seen.set(item.book.id, item.book);
    }
  }
  return Array.from(seen.values());
}

function BookCard({ book }: { book: ReceivedBookBook }) {
  const imageSrc = book.cover_optimized || book.cover || FALLBACK_COVER;

  return (
    <Tooltip title={book.title} arrow placement="top">
      <Box
        component={Link}
        to={`/books/edit/${book.id}`}
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 0.5,
          textDecoration: "none",
          color: "text.primary",
          "&:hover .book-title": { color: "primary.main" },
        }}
      >
        <Avatar src={imageSrc} alt={book.title} shape="square" fullWidth />
        <Typography
          className="book-title"
          variant="caption"
          sx={{
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            lineHeight: 1.2,
          }}
        >
          {book.title}
        </Typography>
      </Box>
    </Tooltip>
  );
}

interface BookSectionProps {
  icon: React.ReactElement;
  label: string;
  items: ReceivedBook[];
}

function BookSection({ icon, label, items }: BookSectionProps) {
  const books = dedupeByBook(items);
  if (books.length === 0) return null;

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
        {icon}
        <Typography variant="subtitle2">{label}</Typography>
        <Chip label={books.length} size="small" />
      </Stack>
      <Grid container spacing={2}>
        {books.map(book => (
          <Grid key={book.id} size={{ xs: 6, sm: 4, md: 3, lg: 2 }}>
            <BookCard book={book} />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}

export const ReceivedBookPeriods = React.memo(({ periods, loading }: ReceivedBookPeriodsProps) => {
  if (loading) {
    return (
      <Stack spacing={1}>
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} variant="rounded" height={56} />
        ))}
      </Stack>
    );
  }

  if (!periods || periods.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
        Nenhum período encontrado.
      </Typography>
    );
  }

  return (
    <Stack spacing={1}>
      {periods.map(period => {
        const grouped = groupByType(period.books);
        const totalCount = period.books.length;

        return (
          <Accordion key={period.label} disableGutters>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Stack
                direction="row"
                alignItems="center"
                spacing={2}
                sx={{ width: "100%", flexWrap: "wrap" }}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 600, textTransform: "capitalize" }}>
                  {period.label}
                </Typography>
                <Chip
                  label={`${totalCount} ${totalCount === 1 ? "mídia" : "mídias"}`}
                  size="small"
                />
                {grouped.books.length > 0 && (
                  <Chip
                    icon={<AutoStoriesIcon />}
                    label={grouped.books.length}
                    size="small"
                    variant="outlined"
                  />
                )}
                {grouped.audiobooks.length > 0 && (
                  <Chip
                    icon={<HeadphonesIcon />}
                    label={grouped.audiobooks.length}
                    size="small"
                    variant="outlined"
                  />
                )}
                {grouped.magazines.length > 0 && (
                  <Chip
                    icon={<NewspaperIcon />}
                    label={grouped.magazines.length}
                    size="small"
                    variant="outlined"
                  />
                )}
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={3}>
                <BookSection
                  icon={<AutoStoriesIcon fontSize="small" />}
                  label="Livros"
                  items={grouped.books}
                />
                <BookSection
                  icon={<HeadphonesIcon fontSize="small" />}
                  label="Audiobooks"
                  items={grouped.audiobooks}
                />
                <BookSection
                  icon={<NewspaperIcon fontSize="small" />}
                  label="Revistas"
                  items={grouped.magazines}
                />
              </Stack>
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Stack>
  );
});
